import { ENRICHMENT_STATE } from '@aibees/elephant/types';
import { Nullable } from '@aibees/shared/definition';
import { forEach } from 'lodash-es';
import {
  EmailItem,
  PeopleEmail,
  PeopleEmailStatus,
  PeopleTableInfo,
} from '../../types';

export function isVerifyingEmails(item?: PeopleTableInfo) {
  return item?.revelation?.email?.state === ENRICHMENT_STATE.PROCESSING;
}

export function getLastVerificationDate(emails?: Nullable<PeopleEmail>) {
  let lastVerifiedDate = emails?.created
    ? new Date(emails?.created).getTime()
    : 0;

  forEach(getEmailsCleared(emails), (item) => {
    if (item.status !== PeopleEmailStatus.NOT_FOUND) {
      lastVerifiedDate = new Date(item.date).getTime();
    }
  });

  if (lastVerifiedDate) return lastVerifiedDate;

  return undefined;
}

export function isAnyEmailNeedsReverification(emails?: Nullable<PeopleEmail>) {
  const now = new Date().getTime();
  let lastVerifiedDate = emails?.created
    ? new Date(emails?.created).getTime()
    : undefined;

  let isThereAnyVerifiedEmail = false;

  forEach(getEmailsCleared(emails), (item) => {
    lastVerifiedDate = new Date(item.date).getTime();
    if (item.status === PeopleEmailStatus.VALID) isThereAnyVerifiedEmail = true;
  });

  return !!(
    emails?.state !== ENRICHMENT_STATE.PROCESSING &&
    isThereAnyVerifiedEmail &&
    lastVerifiedDate &&
    lastVerifiedDate + 86400000 < now
  );
}

// todo: refactor this function to a hook
export function howManyEmailsFound(
  emails?: EmailItem[],
  options?: { countInvalid?: boolean }
) {
  const notCountableEmailStatuses = [
    PeopleEmailStatus.ERROR,
    PeopleEmailStatus.INVALID,
    PeopleEmailStatus.NOT_FOUND,
    PeopleEmailStatus.UNKNOWN,
    PeopleEmailStatus.DOMAIN_NOT_FOUND,
    PeopleEmailStatus.DOMAIN_NOT_EXIST,
    PeopleEmailStatus.DOMAIN_NOT_ACCEPT,
    PeopleEmailStatus.DO_NOT_MAIL,
  ];

  const invalidStatusesList = options?.countInvalid
    ? notCountableEmailStatuses.filter(
        (status) => status !== PeopleEmailStatus.INVALID
      )
    : notCountableEmailStatuses;

  let count = 0;

  forEach(emails, (item) => {
    if (!invalidStatusesList.includes(item.status)) count += 1;
  });
  return count;
}

export function getEmailType(item: EmailItem) {
  return 'Work';
  // NOTICE: for now we just have work emails, until the back-end do some update for personal emails
  // return item.free ? 'Work' : 'Personal';
}

export function getEmailKind(index: number, compact: boolean) {
  if (index === 0) return compact ? 'P' : 'Primary';
  if (index === 1) return compact ? 'S' : 'Secondary';
  return compact ? 'O' : 'Other';
}

export function getEmailsCleared(emails?: Nullable<PeopleEmail>) {
  const tempList = emails?.content?.filter(
    (email) =>
      email.status !== PeopleEmailStatus.ERROR &&
      email.status !== PeopleEmailStatus.NOT_FOUND &&
      email.status !== PeopleEmailStatus.UNKNOWN &&
      email.status !== PeopleEmailStatus.DOMAIN_NOT_FOUND &&
      email.status !== PeopleEmailStatus.DOMAIN_NOT_EXIST &&
      email.status !== PeopleEmailStatus.DOMAIN_NOT_ACCEPT &&
      email.status !== PeopleEmailStatus.DO_NOT_MAIL
  );

  if (tempList?.length) {
    return tempList;
  }
  return [
    {
      address: 'Email Not Found',
      status: PeopleEmailStatus.NOT_FOUND,
      free: false,
      found: false,
      new: false,
      date: '0',
      score: 0,
    },
  ] as EmailItem[];
}

export function getEmailQucikInfoStatus(emails?: EmailItem[]) {
  const statuses = emails?.map((i) => i.status);
  if (statuses?.includes(PeopleEmailStatus.VALID))
    return PeopleEmailStatus.VALID;
  if (statuses?.includes(PeopleEmailStatus.CATCH_ALL))
    return PeopleEmailStatus.CATCH_ALL;
  if (statuses?.includes(PeopleEmailStatus.INVALID))
    return PeopleEmailStatus.INVALID;

  return PeopleEmailStatus.LOCKED;
}

// isEmailBelongsToPreviousCompanies lived here. It was `hasAnyFoundEmail &&
// isJobChangeHappened`, which made it a second name for the same predicate once
// the found-email precondition was dropped — a never-unlocked contact whose
// company changed must also be offered "Update Profile & Find Email", not a
// plain Unlock. Its callers now use isJobChangeHappened directly.

const DAY_IN_MS = 86400000;

/** How long a not-found email has to sit before Find Email may be run again. */
export const EMAIL_NOT_FOUND_RETRY_DAYS = 21;

/**
 * Find Email ran, came back with nothing, and the cooldown has since expired —
 * so the ordinary CONTACT_REVEAL_EMAIL becomes available again, offered as
 * "Retry".
 *
 * Deliberately reads `revelation.email.content` raw rather than going through
 * `getEmailsCleared`: that helper filters real NOT_FOUND items out and
 * substitutes a synthetic `{ address: 'Email Not Found' }`, so downstream of it
 * the address is never empty and this would always answer "found". Do not
 * refactor it onto the cleared list.
 */
export function isEmailNotFoundRetryable(data?: PeopleTableInfo) {
  const email = data?.revelation?.email;
  if (!email?.created) return false;

  // The backend reports "not found" as a null address.
  const first = email.content?.[0];
  if (!first || first.address !== null) return false;

  // Measured from the revelation, not the item — and every write to
  // revelation.email restamps `created`, so a retry closes this window itself.
  const attemptedAt = new Date(email.created).getTime();
  if (!attemptedAt) return false;

  return attemptedAt + EMAIL_NOT_FOUND_RETRY_DAYS * DAY_IN_MS < Date.now();
}
